import React, { useState } from 'react';

// Define types for our component
interface CsvToXmlProps { }

// CSV to XML conversion function
const csvToXml = (
    csv: string,
    options: {
        rootElement: string;
        rowElement: string;
        header: boolean;
        delimiter: string;
        trimValues: boolean;
        skipEmptyLines: boolean;
        xmlDeclaration: boolean;
        prettyPrint: boolean;
        attributeMode: boolean;
    }
): string => {
    try {
        // Split the CSV by lines
        const lines = csv.split(/\r?\n/);

        // Handle empty CSV
        if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
            return '';
        }

        // Parse the headers if they exist
        let headers: string[] = [];
        let startRow = 0;

        if (options.header) {
            // Use the first row as headers
            const headerRow = lines[0];
            headers = headerRow
                .split(options.delimiter)
                .map(header => options.trimValues ? header.trim() : header)
                // Remove any special characters from headers that aren't XML-friendly
                .map(header => sanitizeXmlName(header));

            startRow = 1;
        } else {
            // Generate default column names
            const firstRow = lines[0].split(options.delimiter);
            headers = firstRow.map((_, index) => `column${index + 1}`);
        }

        // Generate XML
        let xml = '';

        // Add XML declaration if requested
        if (options.xmlDeclaration) {
            xml += '<?xml version="1.0" encoding="UTF-8"?>\n';
        }

        // Start root element
        xml += `<${options.rootElement}>${options.prettyPrint ? '\n' : ''}`;

        // Process each row
        for (let i = startRow; i < lines.length; i++) {
            const line = lines[i].trim();

            // Skip empty lines if configured to do so
            if (options.skipEmptyLines && line === '') {
                continue;
            }

            const values = parseCSVLine(line, options.delimiter);

            if (values.length > 0) {
                if (options.prettyPrint) {
                    xml += '  ';
                }

                xml += `<${options.rowElement}`;

                if (options.attributeMode) {
                    // Add attributes to row element
                    for (let j = 0; j < headers.length; j++) {
                        if (j < values.length) {
                            const value = values[j];
                            const trimmedValue = options.trimValues ? value.trim() : value;

                            // Only add non-empty values as attributes
                            if (trimmedValue !== '') {
                                xml += ` ${headers[j]}="${escapeXmlValue(trimmedValue)}"`;
                            }
                        }
                    }
                    xml += `/>`;
                } else {
                    // Add child elements to row element
                    xml += `>${options.prettyPrint ? '\n' : ''}`;

                    for (let j = 0; j < headers.length; j++) {
                        if (j < values.length) {
                            const value = values[j];
                            const trimmedValue = options.trimValues ? value.trim() : value;

                            if (options.prettyPrint) {
                                xml += '    ';
                            }

                            xml += `<${headers[j]}>${escapeXmlValue(trimmedValue)}</${headers[j]}>`;

                            if (options.prettyPrint) {
                                xml += '\n';
                            }
                        }
                    }

                    if (options.prettyPrint) {
                        xml += '  ';
                    }

                    xml += `</${options.rowElement}>`;
                }

                if (options.prettyPrint) {
                    xml += '\n';
                }
            }
        }

        // Close root element
        xml += `</${options.rootElement}>`;

        return xml;
    } catch (error) {
        throw error;
    }
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
    const values: string[] = [];
    let currentValue = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            // Handle escaped double quotes
            if (i + 1 < line.length && line[i + 1] === '"') {
                currentValue += '"';
                i++; // Skip the next quote
            } else {
                // Toggle the quote state
                insideQuotes = !insideQuotes;
            }
        } else if (char === delimiter && !insideQuotes) {
            // End of field
            values.push(currentValue);
            currentValue = '';
        } else {
            // Regular character
            currentValue += char;
        }
    }

    // Add the last value
    values.push(currentValue);

    return values;
};

// Helper function to sanitize XML element names
const sanitizeXmlName = (name: string): string => {
    // Replace spaces and special characters with underscores
    let sanitized = name.replace(/[^\w.-]/g, '_');

    // XML names cannot start with a number, the characters xml (in any case), or special characters
    if (/^[0-9]/.test(sanitized) || /^xml/i.test(sanitized) || /^[.-]/.test(sanitized)) {
        sanitized = '_' + sanitized;
    }

    return sanitized;
};

// Helper function to escape XML special characters
const escapeXmlValue = (value: string): string => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};

// Main component to convert CSV to XML
const CsvToXml: React.FC<CsvToXmlProps> = () => {
    // State for the CSV input
    const [csvInput, setCsvInput] = useState<string>('');
    // State for the XML output
    const [xmlOutput, setXmlOutput] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing advanced options
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isXmlGenerated, setIsXmlGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);

    // Conversion options
    const [rootElement, setRootElement] = useState<string>('root');
    const [rowElement, setRowElement] = useState<string>('row');
    const [hasHeader, setHasHeader] = useState<boolean>(true);
    const [delimiter, setDelimiter] = useState<string>(',');
    const [trimValues, setTrimValues] = useState<boolean>(true);
    const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
    const [xmlDeclaration, setXmlDeclaration] = useState<boolean>(true);
    const [prettyPrint, setPrettyPrint] = useState<boolean>(true);
    const [attributeMode, setAttributeMode] = useState<boolean>(false);

    // Handle CSV input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCsvInput(e.target.value);
        setError('');
        setIsXmlGenerated(false);
    };

    // Handle file upload
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Update the element names based on the uploaded file
        const nameParts = file.name.split('.');
        if (nameParts.length > 1) {
            nameParts.pop(); // Remove extension
            const baseName = nameParts.join('_').toLowerCase().replace(/[^\w_]/g, '_');
            setRootElement(baseName + 's');
            setRowElement(baseName);
        }

        // Read the file content
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                setCsvInput(event.target.result as string);
                setError('');
                setIsXmlGenerated(false);
            }
        };
        reader.onerror = () => {
            setError('Error reading the file');
        };
        reader.readAsText(file);
    };

    // Use example data
    const useExampleData = () => {
        const exampleCsv = `Name,Age,Email,IsActive,JoinDate,Score
John Doe,32,john@example.com,true,2021-03-15,85.5
Jane Smith,28,jane@example.com,true,2020-11-20,92.0
Bob Johnson,45,bob@example.com,false,2019-07-08,76.8
Alice Brown,33,alice@example.com,true,2022-01-10,88.2
Charlie Wilson,,charlie@example.com,false,2020-02-28,`;

        setCsvInput(exampleCsv);
        setError('');
        setIsXmlGenerated(false);
        setXmlOutput('');
        setRootElement('users');
        setRowElement('user');
    };

    // Convert CSV to XML
    const convertCsvToXml = () => {
        setError('');
        setIsLoading(true);
        setIsXmlGenerated(false);
        setXmlOutput('');

        if (!csvInput.trim()) {
            setError('Please enter CSV data');
            setIsLoading(false);
            return;
        }

        try {
            // Generate the XML
            const options = {
                rootElement,
                rowElement,
                header: hasHeader,
                delimiter,
                trimValues,
                skipEmptyLines,
                xmlDeclaration,
                prettyPrint,
                attributeMode
            };

            const xmlContent = csvToXml(csvInput, options);
            setXmlOutput(xmlContent);

            setIsXmlGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting CSV to XML: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setCsvInput('');
        setXmlOutput('');
        setError('');
        setRootElement('root');
        setRowElement('row');
        setIsXmlGenerated(false);
    };

    // Copy XML to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(xmlOutput)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download XML file
    const downloadXml = () => {
        const blob = new Blob([xmlOutput], { type: 'application/xml;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${rootElement.replace(/[^\w_]/g, '_')}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">CSV to XML Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert CSV (Comma-Separated Values) data to XML (Extensible Markup Language). Upload a CSV file or paste your data below.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="csvInput" className="font-medium text-gray-700">
                                CSV Input
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleData}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setCsvInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <div className="mb-4">
                            <input
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileUpload}
                                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
                            />
                        </div>

                        <textarea
                            id="csvInput"
                            className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={csvInput}
                            onChange={handleInputChange}
                            placeholder="Paste your CSV data here, e.g.,
Name,Age,Email
John Doe,30,john@example.com
Jane Smith,25,jane@example.com"
                        />
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="rootElement" className="block text-sm font-medium text-gray-700 mb-1">
                                Root Element Name
                            </label>
                            <input
                                type="text"
                                id="rootElement"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={rootElement}
                                onChange={(e) => setRootElement(sanitizeXmlName(e.target.value))}
                                placeholder="Enter root element name (e.g., users)"
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="rowElement" className="block text-sm font-medium text-gray-700 mb-1">
                                Row Element Name
                            </label>
                            <input
                                type="text"
                                id="rowElement"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={rowElement}
                                onChange={(e) => setRowElement(sanitizeXmlName(e.target.value))}
                                placeholder="Enter row element name (e.g., user)"
                            />
                        </div>

                        <div className="mb-4">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                            >
                                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showAdvanced && (
                            <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="hasHeader"
                                        checked={hasHeader}
                                        onChange={(e) => setHasHeader(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                                        First row contains headers
                                    </label>
                                </div>

                                <div>
                                    <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                                        Delimiter
                                    </label>
                                    <select
                                        id="delimiter"
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        value={delimiter}
                                        onChange={(e) => setDelimiter(e.target.value)}
                                    >
                                        <option value=",">Comma (,)</option>
                                        <option value=";">Semicolon (;)</option>
                                        <option value="\t">Tab</option>
                                        <option value="|">Pipe (|)</option>
                                    </select>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="trimValues"
                                        checked={trimValues}
                                        onChange={(e) => setTrimValues(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                                        Trim whitespace from values
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="skipEmptyLines"
                                        checked={skipEmptyLines}
                                        onChange={(e) => setSkipEmptyLines(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                                        Skip empty lines
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="xmlDeclaration"
                                        checked={xmlDeclaration}
                                        onChange={(e) => setXmlDeclaration(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="xmlDeclaration" className="ml-2 block text-sm text-gray-700">
                                        Include XML declaration
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="prettyPrint"
                                        checked={prettyPrint}
                                        onChange={(e) => setPrettyPrint(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="prettyPrint" className="ml-2 block text-sm text-gray-700">
                                        Pretty print (format XML with indentation)
                                    </label>
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="attributeMode"
                                        checked={attributeMode}
                                        onChange={(e) => setAttributeMode(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="attributeMode" className="ml-2 block text-sm text-gray-700">
                                        Use attributes instead of elements for values
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertCsvToXml}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Convert to XML'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            XML Output
                        </label>

                        {isXmlGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={copyToClipboard}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied ? 'Copied!' : 'Copy XML'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={downloadXml}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isXmlGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    <p>
                                        XML output will appear here after conversion.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isXmlGenerated && (
                            <div className="flex-grow flex flex-col">
                                <div className="flex-grow flex flex-col">
                                    <textarea
                                        id="xmlOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                        value={xmlOutput}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {isXmlGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">CSV Successfully Converted to XML!</p>
                            </div>
                            <p className="text-sm">
                                You can now copy or download the generated XML for your applications.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About CSV to XML Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
                        <p className="text-gray-600">
                            This tool converts CSV (Comma-Separated Values) data into XML (Extensible Markup Language) format. The converter parses each row of your CSV file and generates a structured XML document with customizable element names. You can choose between element-based or attribute-based representation of your data to suit your specific needs.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Customizable Elements</strong> - Define your own root and row element names</li>
                            <li><strong>Attribute Mode</strong> - Option to represent data as XML attributes instead of nested elements</li>
                            <li><strong>Pretty Printing</strong> - Format XML with indentation for better readability</li>
                            <li><strong>XML Declaration</strong> - Option to include or exclude XML declaration</li>
                            <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
                            <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
                            <li><strong>Custom Headers</strong> - Use first row as element names or generate default names</li>
                            <li><strong>Value Trimming</strong> - Option to trim whitespace from field values</li>
                            <li><strong>Empty Line Handling</strong> - Skip or include empty lines as needed</li>
                            <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
                            <li><strong>Special Character Escaping</strong> - Converts special XML characters to their entity references</li>
                        </ul>
                    </div>

                </div>
            </div>
        </div>
    )
};
export default CsvToXml;